import '@/index.css';
import '@/App.css';
import { BrowserRouter, Routes, Route, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import Layout from '@/components/Layout';
import Login from '@/pages/Login';
import LandingPage from '@/pages/LandingPage';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import VerifyEmail from '@/pages/VerifyEmail';
import AccountSettings from '@/pages/AccountSettings';
import About from '@/pages/About';
import Privacy from '@/pages/Privacy';
import Terms from '@/pages/Terms';
import Disclaimer from '@/pages/Disclaimer';
import RefundPolicy from '@/pages/RefundPolicy';
import DailyMarketConditions from '@/pages/DailyMarketConditions';
import TradeSetupValidator from '@/pages/TradeSetupValidator';
import TradeJournal from '@/pages/TradeJournal';
import WeeklyReview from '@/pages/WeeklyReview';
import MyTradingPlan from '@/pages/MyTradingPlan';
import SignalScanner from '@/pages/SignalScanner';
import RiskManager from '@/pages/RiskManager';
import PerformanceTracker from '@/pages/PerformanceTracker';
import PatternPerformance from '@/pages/PatternPerformance';
import AutomationReadiness from '@/pages/AutomationReadiness';
import PaperTrading from '@/pages/PaperTrading';
import AdminDashboard from '@/pages/AdminDashboard';
import useFirstRealSignalMoment from '@/hooks/useFirstRealSignalMoment';
import { Toaster } from '@/components/ui/sonner';
import CookieNotice from '@/components/CookieNotice';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-primary animate-spin" />
    </div>
  );
}

function PublicLanding() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialShowLogin = searchParams.get('login') === '1';
  const [showLogin, setShowLogin] = useState(initialShowLogin);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  if (showForgotPassword) {
    return <ForgotPassword onBack={() => setShowForgotPassword(false)} />;
  }
  // Legacy `?login=1` deep-link still works — opens the dedicated full-screen Login page.
  if (showLogin) {
    return (
      <Login
        onLogin={login}
        onForgotPassword={() => {
          setShowLogin(false);
          setShowForgotPassword(true);
        }}
      />
    );
  }
  return (
    <LandingPage
      onLogin={login}
      onForgotPassword={() => setShowForgotPassword(true)}
      onShowAbout={() => navigate('/about')}
    />
  );
}

function AuthGatedRoutes() {
  const { user, token, loading } = useAuth();
  const navigate = useNavigate();

  useFirstRealSignalMoment({
    enabled: !!user,
    onNavigate: () => navigate('/signals'),
    token,
  });

  if (loading) return <LoadingScreen />;
  if (!user) return <PublicLanding />;

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<DailyMarketConditions />} />
        <Route path="/validator" element={<TradeSetupValidator />} />
        <Route path="/journal" element={<TradeJournal />} />
        <Route path="/review" element={<WeeklyReview />} />
        <Route path="/plan" element={<MyTradingPlan />} />
        <Route path="/signals" element={<SignalScanner />} />
        <Route path="/risk-manager" element={<RiskManager />} />
        <Route path="/performance" element={<PerformanceTracker />} />
        <Route path="/pattern-performance" element={<PatternPerformance />} />
        <Route path="/automation-readiness" element={<AutomationReadiness />} />
        <Route path="/paper-trading" element={<PaperTrading />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/account" element={<AccountSettings />} />
      </Routes>
    </Layout>
  );
}

function AppContent() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify" element={<VerifyEmail />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/about" element={<About />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/disclaimer" element={<Disclaimer />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/*" element={<AuthGatedRoutes />} />
      </Routes>
      <CookieNotice />
    </BrowserRouter>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <AppContent />
      </AuthProvider>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
